import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  SetProposalDepositInput,
  SetProposalDepositMutation,
} from '@/gql/graphql';

const SET_PROPOSAL_DEPOSIT_MUTATION = graphql(`
  mutation SetProposalDeposit($input: SetProposalDepositInput!) {
    setProposalDeposit(input: $input) {
      id
    }
  }
`);

/**
 * Set or clear the deposit requested on a Proposal. Provide exactly one of
 * depositPercentage or depositAmount, or neither to clear. The server rejects
 * while the Proposal has a payment schedule — the two are mutually exclusive.
 */
const useSetProposalDeposit = (args?: {
  proposalId?: string;
  options?: UseMutationOptions<
    SetProposalDepositMutation,
    unknown,
    SetProposalDepositInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: SetProposalDepositInput) => {
      return gqlClient.request(SET_PROPOSAL_DEPOSIT_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useSetProposalDeposit;
